"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.suspendDesktopLicenseRequest = exports.updateDesktopLicenseRequest = exports.createDesktopLicenseRequest = exports.issuedDesktopLicense = exports.desktopLicenseDetail = exports.desktopLicenseDto = exports.desktopDeviceDto = exports.desktopHeartbeatResponse = exports.desktopHeartbeatRequest = exports.desktopActivateResponse = exports.desktopActivateRequest = exports.signedDesktopLease = exports.desktopLease = exports.desktopLeaseStatus = exports.desktopLicenseStatus = exports.licenseKey = exports.LICENSE_KEY_ALPHABET = void 0;
exports.evaluateDesktopLease = evaluateDesktopLease;
const zod_1 = require("zod");
const common_1 = require("./common");
// ---------------------------------------------------------------------------
// تراخيص نسخة سطح المكتب — تُصدر وتُوقف من لوحة المنصة، والبرنامج يسأل عنها
// ---------------------------------------------------------------------------
/**
 * صيغة المفتاح: `HSB-` ثم أربع مجموعات من خمسة حروف Crockford base32 = ١٠٠ بت.
 * الحروف المتشابهة (I L O U) غير مستعملة، فالمفتاح يُملى بالتليفون بلا لبس.
 */
exports.LICENSE_KEY_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
exports.licenseKey = zod_1.z
    .string()
    .trim()
    .toUpperCase()
    .transform((value) => value.replace(/[\s_]+/g, '-'))
    .pipe(zod_1.z.string().regex(/^HSB(-[0-9A-HJKMNP-TV-Z]{5}){4}$/, 'مفتاح التفعيل غير صحيح'));
exports.desktopLicenseStatus = zod_1.z.enum(['active', 'suspended']);
/**
 * حالة الجهاز كما يُبلَّغ بها البرنامج. كل ما ليس `active` يحوّل الشركة على الجهاز
 * للقراءة فقط: الدفتر يبقى مقروءًا وقابلاً للتصدير، ولا يُحذف شىء (blueprint/18).
 */
exports.desktopLeaseStatus = zod_1.z.enum(['active', 'suspended', 'revoked', 'expired']);
/** ما يوقّعه الخادم. البرنامج يحفظه ويعمل به بلا إنترنت حتى `leaseUntil`. */
exports.desktopLease = zod_1.z.object({
    v: zod_1.z.literal(1),
    licenseId: common_1.uuid,
    deviceId: common_1.uuid,
    status: exports.desktopLeaseStatus,
    reason: zod_1.z.string().nullable(),
    customerName: zod_1.z.string(),
    validUntil: zod_1.z.string().nullable(),
    issuedAt: zod_1.z.string(),
    leaseUntil: zod_1.z.string(),
});
/**
 * التوقيع على نص `payload` نفسه (base64url لـ JSON) لا على كائن يُعاد تسلسله —
 * فلا خلاف فى ترتيب المفاتيح بين الخادم والبرنامج. Ed25519.
 */
exports.signedDesktopLease = zod_1.z.object({
    payload: zod_1.z.string().min(1),
    signature: zod_1.z.string().min(1),
});
const deviceName = zod_1.z.string().trim().min(1).max(100);
const appVersion = zod_1.z.string().trim().min(1).max(32);
const companyName = zod_1.z.string().trim().min(1).max(200).nullable().optional();
exports.desktopActivateRequest = zod_1.z
    .object({
    licenseKey: exports.licenseKey,
    deviceId: common_1.uuid,
    deviceName,
    appVersion,
    companyName,
})
    .strict();
exports.desktopActivateResponse = zod_1.z.object({
    /** يُعرض مرة واحدة ويُحفظ معمّى على الجهاز — به يثبت الجهاز هويته فى كل نبض. */
    deviceToken: zod_1.z.string(),
    lease: exports.signedDesktopLease,
});
exports.desktopHeartbeatRequest = zod_1.z
    .object({
    deviceId: common_1.uuid,
    deviceToken: zod_1.z.string().min(32).max(128),
    appVersion,
    companyName,
})
    .strict();
exports.desktopHeartbeatResponse = zod_1.z.object({ lease: exports.signedDesktopLease });
/** ساعة الجهاز أقدم من آخر عقد بأكثر من يوم = رُجعت للخلف لتمديد العمل بلا إنترنت. */
const CLOCK_SKEW_MS = 24 * 60 * 60 * 1000;
function evaluateDesktopLease(lease, deviceId, now) {
    if (!lease || lease.deviceId !== deviceId) {
        return {
            writable: false,
            status: 'unlicensed',
            message: 'النسخة غير مفعّلة. أدخل مفتاح التفعيل من «ملف ← الترخيص».',
        };
    }
    if (now.getTime() < new Date(lease.issuedAt).getTime() - CLOCK_SKEW_MS) {
        return {
            writable: false,
            status: 'clock',
            message: 'تاريخ الجهاز أقدم من آخر تحقق من الترخيص. اضبط التاريخ والساعة ثم أعد التشغيل.',
        };
    }
    if (lease.status === 'suspended') {
        return {
            writable: false,
            status: 'suspended',
            message: 'النسخة موقوفة من مزوّد الخدمة' +
                (lease.reason ? ': ' + lease.reason : '') +
                '. البيانات محفوظة ومتاحة للقراءة والتصدير.',
        };
    }
    if (lease.status === 'revoked') {
        return {
            writable: false,
            status: 'revoked',
            message: 'تفعيل هذا الجهاز أُلغى من مزوّد الخدمة. أدخل مفتاح التفعيل من جديد.',
        };
    }
    if (lease.status === 'expired' ||
        (lease.validUntil && now.getTime() > new Date(lease.validUntil).getTime())) {
        return {
            writable: false,
            status: 'expired',
            message: 'انتهى الاشتراك. تواصل مع مزوّد الخدمة للتجديد — البيانات محفوظة ومتاحة للقراءة.',
        };
    }
    if (now.getTime() > new Date(lease.leaseUntil).getTime()) {
        return {
            writable: false,
            status: 'expired',
            message: 'لم يتصل البرنامج بالإنترنت منذ مدة طويلة. وصّل الجهاز بالإنترنت ليتحقق من الترخيص.',
        };
    }
    return { writable: true, status: 'active', message: 'النسخة مفعّلة لـ ' + lease.customerName };
}
// --- لوحة المنصة -----------------------------------------------------------
exports.desktopDeviceDto = zod_1.z.object({
    id: common_1.uuid,
    deviceName: zod_1.z.string(),
    companyName: zod_1.z.string().nullable(),
    appVersion: zod_1.z.string().nullable(),
    activatedAt: zod_1.z.string(),
    lastSeenAt: zod_1.z.string(),
    revokedAt: zod_1.z.string().nullable(),
});
exports.desktopLicenseDto = zod_1.z.object({
    id: common_1.uuid,
    keyHint: zod_1.z.string(),
    customerName: zod_1.z.string(),
    notes: zod_1.z.string().nullable(),
    status: exports.desktopLicenseStatus,
    maxDevices: zod_1.z.number().int(),
    validUntil: zod_1.z.string().nullable(),
    suspendedAt: zod_1.z.string().nullable(),
    suspendedReason: zod_1.z.string().nullable(),
    createdAt: zod_1.z.string(),
    activeDevices: zod_1.z.number().int(),
    /** آخر نبض من أى جهاز — `null` = لم يُفعَّل بعد. */
    lastSeenAt: zod_1.z.string().nullable(),
    /** أحدث إصدار يعمل عليه جهاز مفعّل. */
    appVersion: zod_1.z.string().nullable(),
});
exports.desktopLicenseDetail = exports.desktopLicenseDto.extend({
    devices: zod_1.z.array(exports.desktopDeviceDto),
    events: zod_1.z.array(zod_1.z.object({
        action: zod_1.z.string(),
        actorEmail: zod_1.z.string().nullable(),
        detail: zod_1.z.unknown(),
        createdAt: zod_1.z.string(),
    })),
});
/** الرد الوحيد الذى يحمل المفتاح كاملاً — عند الإصدار أو التجديد. */
exports.issuedDesktopLicense = zod_1.z.object({
    license: exports.desktopLicenseDetail,
    licenseKey: zod_1.z.string(),
});
exports.createDesktopLicenseRequest = zod_1.z
    .object({
    customerName: zod_1.z.string().trim().min(1).max(200),
    maxDevices: zod_1.z.number().int().min(1).max(1000).default(1),
    validUntil: common_1.isoDate.nullable().optional(),
    notes: zod_1.z.string().trim().max(1000).nullable().optional(),
})
    .strict();
exports.updateDesktopLicenseRequest = zod_1.z
    .object({
    customerName: zod_1.z.string().trim().min(1).max(200).optional(),
    maxDevices: zod_1.z.number().int().min(1).max(1000).optional(),
    validUntil: common_1.isoDate.nullable().optional(),
    notes: zod_1.z.string().trim().max(1000).nullable().optional(),
})
    .strict()
    .refine((body) => Object.keys(body).length > 0, 'nothing to update');
exports.suspendDesktopLicenseRequest = zod_1.z
    .object({ reason: zod_1.z.string().trim().min(1).max(500) })
    .strict();
//# sourceMappingURL=desktop-license.js.map